import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ProfileProjectsList } from '../../project/components/ProfileProjectsList';
import { useProfileProjects } from '@/src/hooks/use-fetch-projects';
import PageLoader from '@/src/components/ui/PageLoader';
import { IProject } from 'types/src/types';
import { ProjectCard } from '../../project/components/ProjectCard';
import { Icon } from '@/src/components/ui';

type Props = {
  userUniqueLink: string;
  unpublishedProjects?: IProject[];
  isMyProfile: boolean;
};

export const ProjectsSection = ({
  userUniqueLink,
  unpublishedProjects,
  isMyProfile,
}: Props) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProfileProjects(userUniqueLink);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const unpublishedScrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const allPages = data?.pages ?? [];
  const projects = allPages.flatMap((page) => page.projects);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const updateArrows = () => {
    const el = unpublishedScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 0);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  const scrollByStep = (dir: 'left' | 'right') => {
    const el = unpublishedScrollRef.current;
    if (!el) return;
    const step = Math.max(300, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
  };
  useEffect(() => {
    updateArrows();
    const el = unpublishedScrollRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [unpublishedProjects?.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loadMore]);

  return (
    <div className="mb-5">
      {isLoading && <PageLoader />}
      {unpublishedProjects && unpublishedProjects.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="md:text-2xl font-semibold flex md:inline flex-col">
              Unpublished Projects
              <span className="ml-2 text-sm font-light text-gray-500">
                (visible only to you)
              </span>
            </h3>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByStep('left')}
                disabled={!canLeft}
                className="inline-flex items-center justify-center size-9 rounded-full bg-white shadow border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Scroll left"
                title="Scroll left"
              >
                <Icon name="left" className="size-3" />
              </button>
              <button
                type="button"
                onClick={() => scrollByStep('right')}
                disabled={!canRight}
                className="inline-flex items-center justify-center size-9 rounded-full bg-white shadow border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Scroll right"
                title="Scroll right"
              >
                <Icon name="right" className="size-3" />
              </button>
            </div>
          </div>

          <div
            ref={unpublishedScrollRef}
            className="flex gap-3 w-full overflow-x-auto hide-scrollbar mb-8 pb-3"
          >
            {unpublishedProjects.map((project) => (
              <div key={project.id}>
                <ProjectCard
                  id={project.id}
                  title={project.title}
                  shortDescription={project.shortDescription}
                  price={project.price ?? null}
                  slug={project.slug}
                  systems={project.systems}
                  rating={project.rating}
                  coverImageUrl={project.coverImageUrl}
                />
              </div>
            ))}
          </div>
          <hr className="mb-6" />
        </>
      )}
      {isMyProfile && !!unpublishedProjects?.length && (
        <h3 className="md:text-2xl font-semibold mb-6">Published Projects</h3>
      )}
      <ProfileProjectsList
        projects={projects}
        loading={isLoading}
        hasNextPage={hasNextPage}
        loadMore={loadMore}
        loadMoreRef={loadMoreRef}
        isProfile={true}
      />
    </div>
  );
};
